import React, { useState, useEffect } from 'react';
import apiClient from '@/services/api';
import MinterUsageCard from '@/components/monitoring/MinterUsageCard';
import QueueHealthCard from '@/components/monitoring/QueueHealthCard';
import AdminCliHealthCard from '@/components/monitoring/AdminCliHealthCard';
import AlertsTable from '@/components/monitoring/AlertsTable';
import AlertsBanner from '@/components/monitoring/AlertsBanner';
import { errorLog } from '@/utils/logger';

interface HybridPayoutMetrics {
  minterUsage: {
    dailyLimit: string;
    usedToday: string;
    remainingToday: string;
    percentageUsed: number;
    lastResetAt: string;
  };
  queueHealth: {
    pendingPayouts: number;
    autoEligible: number;
    manualRequired: number;
    pendingSignatures: number;
    oldestPendingMinutes: number;
    averagePendingMinutes: number;
  };
  adminCli: {
    lastPollAt: string | null;
    isHealthy: boolean;
    minutesSinceLastPoll: number | null;
  };
  transactionStats: {
    failed24h: number;
    total24h: number;
    failureRate: number;
  };
  alerts: {
    active: number;
    critical: number;
  };
}

interface Alert {
  id: string;
  type: string;
  severity: string;
  title: string;
  message: string;
  metadata?: any;
  userId?: string;
  transactionHash?: string;
  walletAddress?: string;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
}

const MonitoringPage: React.FC = () => {
  const [metrics, setMetrics] = useState<HybridPayoutMetrics | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval] = useState(30); // seconds

  const fetchMetrics = async () => {
    try {
      const [metricsRes, alertsRes] = await Promise.all([
        apiClient.monitoring.getHybridPayoutMetrics(),
        apiClient.monitoring.getAlerts(50),
      ]);

      if (metricsRes.success) {
        setMetrics(metricsRes.data);
      }

      if (alertsRes.success) {
        setAlerts(alertsRes.data);
      }

      setLoading(false);
    } catch (error) {
      errorLog('Error fetching monitoring data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(fetchMetrics, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  const handleDismissAlert = async (alertId: string) => {
    try {
      await apiClient.monitoring.dismissAlert(alertId);
      await fetchMetrics(); // Refresh data
    } catch (error) {
      errorLog('Error dismissing alert:', error);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchMetrics();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-2 text-gray-600">Loading monitoring data...</p>
        </div>
      </div>
    );
  }

  const criticalAlerts = alerts.filter(a => a.severity === 'critical');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">System Monitoring</h1>
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="btn-secondary disabled:opacity-50"
          >
            Refresh
          </button>
          <label className="flex items-center gap-2 text-sm font-semibold text-white cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Auto-refresh ({refreshInterval}s)</span>
          </label>
        </div>
      </div>

      {/* Critical Alerts Banner */}
      {criticalAlerts.length > 0 && (
        <AlertsBanner alerts={criticalAlerts} onDismiss={handleDismissAlert} />
      )}

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MinterUsageCard metrics={metrics?.minterUsage || null} />
        <QueueHealthCard metrics={metrics?.queueHealth || null} />
        <AdminCliHealthCard metrics={metrics?.adminCli || null} />
      </div>

      {/* Transaction Stats Summary */}
      {metrics && (
        <div className="card p-6 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-500 mb-4">Transaction Statistics (24h)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{metrics.transactionStats.total24h}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-red-600">{metrics.transactionStats.failed24h}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Failure Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.transactionStats.failureRate.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Alerts Table */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Active Alerts ({alerts.length})
        </h3>
        <AlertsTable alerts={alerts} onDismiss={handleDismissAlert} />
      </div>
    </div>
  );
};

export default MonitoringPage;
